import { createClient } from '@connectrpc/connect';
import { SharedParameterService } from './gen/shared_parameter_service_pb';
import {
  pollRevitOperation,
  type RevitOperationProgressSnapshot,
} from './operationProgressClient';
import { getGrpcTransport } from './transport';
import { webLog } from './webLogger';
import { useIntegrationDiagnosticsStore } from '../../stores/useIntegrationDiagnosticsStore';
import { mapProjectUndoEntries } from './parameterUndoMappers';
import type { ConnectorProjectParameterUndoEntry } from '../../types/parameterSetupUndo';

export type ProjectParameterBindingKind = 'type' | 'instance';

export type ProjectParameterTerminalStatus =
  | 'Created'
  | 'Updated'
  | 'AlreadyExistsCompatible'
  | 'Conflict'
  | 'Failed';

const PROJECT_PARAMETER_TERMINAL_STATUSES: readonly ProjectParameterTerminalStatus[] = [
  'Created',
  'Updated',
  'AlreadyExistsCompatible',
  'Conflict',
  'Failed',
];

interface ExpectedDocumentIdentity {
  expectedDocumentName?: string;
  expectedDocumentPath?: string;
}

export interface AddProjectParametersOptions {
  bindingKind?: ProjectParameterBindingKind;
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
  parameterTypeHints?: Array<{ name: string; dataType: string }>;
}

export interface AddProjectParametersResult {
  success: boolean;
  addedParametersCount?: number;
  addedParameters?: string[];
  categories?: string[];
  message?: string;
  error?: string;
  undoEntries?: ConnectorProjectParameterUndoEntry[];
  results?: Array<{
    parameter?: string;
    status?: ProjectParameterTerminalStatus;
    success?: boolean;
    message?: string;
    error?: string;
  }>;
}

function normalizeTerminalStatus(value: unknown): ProjectParameterTerminalStatus | undefined {
  return typeof value === 'string' &&
    PROJECT_PARAMETER_TERMINAL_STATUSES.includes(value as ProjectParameterTerminalStatus)
    ? value as ProjectParameterTerminalStatus
    : undefined;
}

function getClient() {
  const transport = getGrpcTransport();
  return transport ? createClient(SharedParameterService, transport) : null;
}

function parseResult(resultJson: string): AddProjectParametersResult {
  const parsed = JSON.parse(resultJson);
  return {
    success: Boolean(parsed.isSuccess),
    addedParametersCount: parsed.addedParametersCount || undefined,
    addedParameters: Array.isArray(parsed.addedParameters) ? parsed.addedParameters : undefined,
    categories: Array.isArray(parsed.categories) ? parsed.categories : undefined,
    message: parsed.message || undefined,
    error: parsed.error || undefined,
    undoEntries: mapProjectUndoEntries(parsed.undoEntries),
    // The host serializes operation results with proto3 JSON semantics, which
    // omit default-valued fields — a failed item arrives without its
    // "success" key. Normalize to the explicit shape the unary path returns.
    results: Array.isArray(parsed.results)
      ? parsed.results.map((item: Record<string, unknown>) => ({
          parameter: typeof item.parameter === 'string' ? item.parameter : '',
          status: normalizeTerminalStatus(item.status),
          success: item.success === true,
          message: typeof item.message === 'string' ? item.message : '',
          error: typeof item.error === 'string' ? item.error : '',
        }))
      : undefined,
  };
}

export async function addProjectParametersGrpc(
  parameterNames: string[],
  categories: string[],
  expectedDocumentIdentity?: ExpectedDocumentIdentity,
  options?: AddProjectParametersOptions,
): Promise<AddProjectParametersResult> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(
      'AddProjectParameters: gRPC transport is unavailable',
    );
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  const request = {
    parameterNames,
    categories,
    expectedDocumentName: expectedDocumentIdentity?.expectedDocumentName ?? '',
    expectedDocumentPath: expectedDocumentIdentity?.expectedDocumentPath ?? '',
    parameterTypeHints: (options?.parameterTypeHints ?? []).map((hint) => ({
      name: hint.name,
      dataType: hint.dataType,
    })),
    bindingKind: options?.bindingKind ?? '',
  };
  webLog.info('parameterSetup', `project parameter request ${JSON.stringify({
    requestedBinding: request.bindingKind || 'unspecified',
    parameterCount: parameterNames.length,
    parameterNames,
    categoryCount: categories.length,
    categories,
  })}`);

  try {
    if (options?.onProgress) {
      const startResponse = await client.startAddProjectParameters(request);
      if (startResponse.isSuccess && startResponse.operationId) {
        const operationResult = await pollRevitOperation(startResponse.operationId, {
          onProgress: options.onProgress,
          parseResult,
        });
        if (!operationResult.result) {
          const error = operationResult.error || 'AddProjectParameters operation failed';
          useIntegrationDiagnosticsStore.getState().setLastGrpcError(`AddProjectParameters: ${error}`);
          return { success: false, error };
        }

        setDiagnostics(operationResult.result);
        return operationResult.result;
      }

      return {
        success: false,
        error: startResponse.error || 'AddProjectParameters did not start a progress operation',
      };
    }

    const response = await client.addProjectParameters(request);
    const result = {
      success: response.isSuccess,
      addedParametersCount: response.addedParametersCount || undefined,
      addedParameters: response.addedParameters || undefined,
      categories: response.categories || undefined,
      message: response.message || undefined,
      error: response.error || undefined,
      undoEntries: mapProjectUndoEntries(response.undoEntries),
      results: response.results?.map((item) => ({
        parameter: item.parameter,
        status: normalizeTerminalStatus(item.status),
        success: item.success,
        message: item.message,
        error: item.error,
      })),
    };
    setDiagnostics(result);
    return result;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'AddProjectParameters gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`AddProjectParameters: ${message}`);
    return { success: false, error: message };
  }
}

function setDiagnostics(result: AddProjectParametersResult): void {
  if (result.success) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
  } else if (result.error) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`AddProjectParameters: ${result.error}`);
  }
}
